"use strict";
/**
 * Server-side rendering of the Content Tool's video block.
 *
 * The editor serialises a video as
 *   <div class="video-embed" data-provider="youtube|vimeo" data-video-id="..."><iframe .../></div>
 * but the Content Tool sanitizer (HtmlSanitizerService) deliberately keeps `iframe` off its
 * allowlist, so what reaches the website is the bare wrapper with its data attributes and no
 * player. We rebuild the iframe here, from the data attributes only — a user-supplied URL is
 * never interpolated into `src`.
 *
 * Rules:
 *  - provider must be exactly `youtube` or `vimeo` (anything else -> block dropped)
 *  - the video id must match the provider's pattern, so it can never break out of the quoted
 *    attribute. The patterns mirror `parseVideoUrl` in @okay/app-content-tool (src/lib/videoUrl.ts)
 *    on purpose: the editor decides what may be saved, and anything it accepted must render here.
 *    A too-short/too-long id then shows the provider's own "video unavailable" player rather than
 *    silently vanishing from the article. Safety comes from the character set, not the length —
 *    [A-Za-z0-9_-] and digits are both inert inside a double-quoted attribute.
 *    (Real YouTube ids are 11 chars and real Vimeo ids 8-9 digits; the bounds are deliberately
 *    wider so the two repos cannot drift into dropping each other's content.)
 *  - YouTube is embedded through youtube-nocookie.com
 *
 * The `.video-embed` wrapper is preserved so article-components.css keeps sizing it 16:9.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.renderVideoEmbeds = exports.buildVideoEmbedSrc = void 0;
var PROVIDERS = {
    youtube: {
        // Mirrors the editor's youtubeIdPattern (videoUrl.ts). Real ids are 11 chars.
        idPattern: /^[A-Za-z0-9_-]{6,20}$/,
        buildSrc: function (videoId) {
            return "https://www.youtube-nocookie.com/embed/" + videoId;
        },
        title: 'YouTube video player',
        allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
    },
    vimeo: {
        // The editor accepts any run of digits; the upper bound is just a sanity cap
        // (real Vimeo ids are 8-9 digits, so it is far out of reach of real content).
        idPattern: /^[0-9]{1,20}$/,
        buildSrc: function (videoId) { return "https://player.vimeo.com/video/" + videoId; },
        title: 'Vimeo video player',
        allow: 'autoplay; fullscreen; picture-in-picture',
    },
};
// <div ... class="... video-embed ..." ...> ... </div>
// The block is an atom whose only child is the (already stripped) iframe, so a non-greedy
// match up to the first </div> is safe; a nested <div> means the markup is not ours and the
// block is left untouched.
var VIDEO_EMBED_BLOCK = /<div([^>]*\sclass\s*=\s*(?:"[^"]*\bvideo-embed\b[^"]*"|'[^']*\bvideo-embed\b[^']*')[^>]*)>([\s\S]*?)<\/div>/gi;
var DATA_PROVIDER = /\bdata-provider\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
var DATA_VIDEO_ID = /\bdata-video-id\s*=\s*(?:"([^"]*)"|'([^']*)')/i;
var NESTED_DIV = /<div[\s>]/i;
var readAttribute = function (attributes, pattern) {
    var match = pattern.exec(attributes);
    if (!match) {
        return '';
    }
    return (match[1] !== undefined ? match[1] : match[2]).trim();
};
/**
 * Builds the embed `src` for a provider/id pair, or null when either fails validation.
 * Exported for the unit tests.
 */
exports.buildVideoEmbedSrc = function (provider, videoId) {
    var spec = PROVIDERS[(provider || '').toLowerCase().trim()];
    if (!spec || !spec.idPattern.test(videoId || '')) {
        return null;
    }
    return spec.buildSrc(videoId);
};
/**
 * Replaces every `div.video-embed[data-provider][data-video-id]` with the same wrapper
 * containing a real iframe. Blocks that fail the provider/id whitelist are removed entirely
 * rather than left as an empty black 16:9 box.
 */
exports.renderVideoEmbeds = function (content) {
    if (!content || content.indexOf('video-embed') === -1) {
        return content;
    }
    return content.replace(VIDEO_EMBED_BLOCK, function (match, attributes, inner) {
        if (NESTED_DIV.test(inner)) {
            return match;
        }
        var provider = readAttribute(attributes, DATA_PROVIDER).toLowerCase();
        var videoId = readAttribute(attributes, DATA_VIDEO_ID);
        var src = exports.buildVideoEmbedSrc(provider, videoId);
        if (!src) {
            return '';
        }
        var spec = PROVIDERS[provider];
        return ("<div class=\"video-embed\" data-provider=\"" + provider + "\" data-video-id=\"" + videoId + "\">" +
            ("<iframe src=\"" + src + "\" title=\"" + spec.title + "\" loading=\"lazy\" frameborder=\"0\" ") +
            ("allow=\"" + spec.allow + "\" allowfullscreen ") +
            "referrerpolicy=\"strict-origin-when-cross-origin\"></iframe>" +
            "</div>");
    });
};
